import { motion } from "framer-motion";
import { HiArrowRight, HiCalendar, HiClock } from "react-icons/hi";
import SectionTitle from "../components/SectionTitle";
import { blogPosts } from "../data/content";
import { fadeInUp, stagger } from "../utils/motion";

function BlogSection() {
  const [featuredPost, ...otherPosts] = blogPosts;

  return (
    <section id="blog" className="relative py-24 sm:py-28">
      <div className="section-shell">
        <SectionTitle
          eyebrow="Blog"
          title="Ideas For Better Digital Products"
          subtitle="Read practical insights from BeeVerge on product planning, automation, UI/UX, web applications, mobile apps, and business growth."
        />

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.article
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="glass group overflow-hidden rounded-2xl shadow-glass"
          >
            <div className="relative h-80 overflow-hidden">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05050b] via-[#05050b]/30 to-transparent" />
              <span className="absolute left-5 top-5 rounded-full border border-cyan-200/25 bg-black/45 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100 backdrop-blur">
                Featured
              </span>
            </div>

            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap gap-3 text-xs text-blue-100/70">
                <span className="rounded-lg border border-cyan-200/15 bg-cyan-300/10 px-3 py-2 font-semibold text-cyan-100">
                  {featuredPost.category}
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2">
                  <HiCalendar className="h-4 w-4 text-cyan-300" />
                  {featuredPost.date}
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2">
                  <HiClock className="h-4 w-4 text-cyan-300" />
                  {featuredPost.readTime}
                </span>
              </div>

              <h3 className="mt-5 text-2xl font-bold leading-tight text-white sm:text-3xl">
                {featuredPost.title}
              </h3>
              <p className="mt-4 text-sm leading-7 text-blue-100/76">{featuredPost.excerpt}</p>
              <a
                href="#contact"
                className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-200 transition hover:text-white sm:text-sm sm:tracking-[0.14em]"
              >
                Read Article
                <HiArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.article>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid gap-5"
          >
            {otherPosts.map((post) => (
              <motion.article
                key={post.title}
                variants={fadeInUp}
                whileHover={{ x: 6 }}
                className="glass grid gap-4 overflow-hidden rounded-2xl p-4 shadow-glass sm:grid-cols-[160px_1fr] lg:grid-cols-[180px_1fr]"
              >
                <img
                  src={post.image}
                  alt={post.title}
                  className="h-48 w-full rounded-xl object-cover sm:h-full"
                />
                <div className="py-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
                    {post.category}
                  </p>
                  <h3 className="mt-3 text-lg font-bold leading-tight text-white sm:text-xl">{post.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-blue-100/74">{post.excerpt}</p>
                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-blue-100/65">
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </motion.article>
            ))}

            <motion.div variants={fadeInUp} className="glass rounded-2xl p-6 shadow-glass">
              <h3 className="text-xl font-bold text-white">Need an article about your project?</h3>
              <p className="mt-3 text-sm leading-6 text-blue-100/74">
                Share your product challenge with BeeVerge and we will help turn the idea into a
                technical roadmap.
              </p>
              <a
                href="#contact"
                className="mt-5 inline-flex items-center gap-2 rounded-lg border border-cyan-200/25 bg-cyan-300/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-100 transition hover:shadow-neon sm:px-5 sm:tracking-[0.14em]"
              >
                Talk To Us
                <HiArrowRight className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default BlogSection;
